"use client"

import { Image, Tag, Text } from "@/elements"
import { JobPostingEmploymentType, JobPostingPayDisplay, JobPostingPayType } from "@/gql/graphql"
import { formatCurrencyAmount } from "@/utils/formatCurrencyAmount"
import { useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { TagsMetadataSection } from "../MetadataSections"
import { JOB_TYPES } from "./constants"
import { PostAJobModalFormValues } from "./types"

const normalizeString = (input: string): string => {
  if (input === "HALF_DAY") {
    return "Half-day"
  }

  const text = input
    .toLowerCase() // Convert to lowercase
    .replace(/_/g, " ") // Replace underscores with spaces

  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const StepThree: React.FC = () => {
  const { watch } = useFormContext<PostAJobModalFormValues>()

  const budgetCurrency = watch("budgetCurrency")

  const rateFrequency = watch("rateFrequency")
  const budgetAmount = watch("budgetAmount")
  const budgetMaxAmount = watch("budgetMaxAmount")
  const skills = watch("skills")
  const description = watch("description")
  const title = watch("title")
  const payType = watch("payType")
  const payDisplay = watch("payDisplay")
  const jobType = watch("jobType")
  const referenceImage = watch("referenceImage")

  const hasSkills = skills?.length > 0

  const jobTypeDisplay = useMemo(() => {
    if (jobType) {
      const label = JOB_TYPES.find(type => type.value === jobType)?.label
      return label
    }
    return null
  }, [jobType])

  const payTypeText = useMemo(() => {
    const isFixedFee = payType === JobPostingPayType.FixedFee
    const isRateBased = payType === JobPostingPayType.RateBased

    const currency = budgetCurrency
    const amount = budgetAmount?.value
    let amountDisplay = amount ? formatCurrencyAmount(amount!, currency) : null
    if (payDisplay === JobPostingPayDisplay.Range) {
      amountDisplay = `${amountDisplay} - ${budgetMaxAmount?.value ? formatCurrencyAmount(budgetMaxAmount?.value!, currency) : null}`
    }

    if (isRateBased) {
      return amount ? `${amountDisplay}/${normalizeString(rateFrequency || "").toLowerCase()} ${currency}` : "Rate TBD"
    } else if (isFixedFee) {
      return `${amountDisplay} ${currency}`
    }
    return "Unpaid"
  }, [payType, budgetCurrency, budgetAmount?.value, payDisplay, budgetMaxAmount?.value, rateFrequency])

  const referenceImageUrl = referenceImage.file || referenceImage?.existingImageUrl

  return (
    <div className="flex flex-col gap-y-8 py-10">
      <Text className="text-fg-2" variant="body2">
        Step 3/3:{" "}
        <Text element="span" variant="body2" className="text-fg-1">
          Review your job
        </Text>
      </Text>

      <div className="flex flex-row-reverse items-center gap-4 sm:flex-row">
        {referenceImageUrl ? (
          <div className="relative size-16 shrink-0 cursor-pointer overflow-hidden rounded-md bg-bg-2 hover:bg-bg-3 sm:size-[80px]">
            <Image
              srcSetUrls={{
                sm: referenceImageUrl,
              }}
              alt="Cover image"
              className="h-full w-full object-cover"
            />
          </div>
        ) : null}
        <Text className="w-full sm:w-auto" variant="h5">
          {title}
        </Text>
      </div>

      <div className="-mt-4 flex flex-col divide-y divide-bg-3">
        {description ? <ReviewSection title="Job description" content={description} /> : null}

        {jobTypeDisplay ? <ReviewSection title="Job type" content={jobTypeDisplay} /> : null}

        {jobType !== JobPostingEmploymentType.Collaborative ? (
          <ReviewSection
            title={payDisplay === JobPostingPayDisplay.Range ? "Pay range" : "Pay type"}
            content={payTypeText}
          />
        ) : null}

        {hasSkills && (
          <div className="flex flex-col gap-y-2 py-4">
            <Text variant="label" className="text-fg-2">
              Skills
            </Text>
            <div className="flex flex-row flex-wrap gap-2">
              {skills.map(skill => {
                return (
                  <div key={skill.id}>
                    <Tag text={skill.name} />
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const ReviewSection = ({ title, content }: { title: string; content: string }) => {
  return (
    <div className="flex flex-col gap-y-2 py-4">
      <Text variant="label" className="text-fg-2">
        {title}
      </Text>
      <Text variant="body1">{content}</Text>
    </div>
  )
}
